import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSearch, FiDownload } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import { today, formatDate, unwrapList } from '../../utils/format';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyLoad = () => ({ machine_id: '', article_no: '', article_name: '', loaded_date: today(), remarks: '' });

export default function MachineArticles() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, per_page: 100, machine_id: '', article_no: '', status: '', from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [machines, setMachines] = useState([]);
  const [articleOptions, setArticleOptions] = useState([]);
  const [loadModal, setLoadModal] = useState(false);
  const [loadForm, setLoadForm] = useState(emptyLoad());
  const [releaseModal, setReleaseModal] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseForm, setReleaseForm] = useState({ released_date: today() });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.machines.articles(params);
      const list = Array.isArray(data.data) ? data.data : data.articles || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load machine articles', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.machines.list({ per_page: 500 }).then(({ data }) => {
      setMachines(unwrapList(data, 'machines'));
    }).catch(() => {});
    api.gatepasses.articles({ per_page: 1000 }).then(({ data }) => {
      const raw = data.articles || data || [];
      const list = Array.isArray(raw) ? raw : [];
      setArticleOptions(list.map((a) => ({
        value: a.article_no,
        label: a.name ? `${a.article_no} — ${a.name}` : a.article_no,
        article_no: a.article_no,
        name: a.name || '',
      })));
    }).catch(() => {});
  }, []);

  const handleLoad = async () => {
    const errs = {};
    if (!loadForm.machine_id) errs.machine_id = 'Machine is required';
    if (!loadForm.article_no) errs.article_no = 'Article No is required';
    if (!loadForm.loaded_date) errs.loaded_date = 'Loaded date is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.machines.loadArticle(Number(loadForm.machine_id), {
        article_no: loadForm.article_no,
        article_name: loadForm.article_name || undefined,
        loaded_date: loadForm.loaded_date,
        remarks: loadForm.remarks || undefined,
      });
      toast('success', '', 'Article loaded on machine');
      setLoadModal(false);
      setLoadForm(emptyLoad());
      load();
    } catch (err) {
      showError('Failed to load article', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const openRelease = (row) => {
    setReleaseTarget(row);
    setReleaseForm({ released_date: today() });
    setErrors({});
    setReleaseModal(true);
  };

  const handleRelease = async () => {
    const errs = {};
    if (!releaseForm.released_date) errs.released_date = 'Released date is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.machines.releaseArticle(releaseTarget.id, releaseForm);
      toast('success', '', 'Article released from machine');
      setReleaseModal(false);
      load();
    } catch (err) {
      showError('Failed to release article', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const res = await confirmDelete(`article "${row.article_no}" record`);
    if (!res.isConfirmed) return;
    try {
      await api.machines.deleteArticle(row.id);
      toast('success', '', 'Record deleted');
      load();
    } catch (err) {
      showError('Failed to delete record', extractError(err));
    }
  };

  const exportCsv = () => {
    const headers = ['Machine', 'Article No', 'Article Name', 'Loaded Date', 'Released Date', 'Status', 'Remarks'];
    const lines = rows.map((r) => [
      r.machine?.name || '',
      r.article_no,
      r.article_name || '',
      r.loaded_date || '',
      r.released_date || '',
      r.released_date ? 'Released' : 'Active',
      r.remarks || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'machine_articles.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <PageHeader
        title="Machine Articles"
        subtitle="Track which article is loaded on each machine and when it was released."
        crumbs={[{ label: 'Production' }, { label: 'Machine Articles' }]}
        actions={
          <>
            {rows.length > 0 && (
              <button className="btn btn-secondary" onClick={exportCsv}><FiDownload /> Export</button>
            )}
            {hasPermission('manage-machine-articles') && (
              <button className="btn btn-primary" onClick={() => { setErrors({}); setLoadForm(emptyLoad()); setLoadModal(true); }}><FiPlus /> Load Article</button>
            )}
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar" style={{ flexWrap: 'wrap' }}>
            <div className="topbar-search" style={{ flex: 1, minWidth: 160 }}>
              <FiSearch className="search-icon" />
              <input placeholder="Search article no..." value={params.article_no}
                onChange={(e) => setParams({ ...params, article_no: e.target.value, page: 1 })} />
            </div>
            <div style={{ minWidth: 180, flex: 1 }}>
              <SearchableSelect
                options={[{ value: '', label: '— All Machines —' }, ...machines.map((m) => ({ value: m.id, label: `${m.name} (${m.head_type}h)` }))]}
                value={params.machine_id}
                onChange={(v) => setParams({ ...params, machine_id: v, page: 1 })}
                placeholder="-- All Machines --"
              />
            </div>
            <select className="form-control" style={{ width: 140 }} value={params.status}
              onChange={(e) => setParams({ ...params, status: e.target.value, page: 1 })}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="released">Released</option>
            </select>
            <input className="form-control" type="date" style={{ width: 150 }} value={params.from}
              onChange={(e) => setParams({ ...params, from: e.target.value, page: 1 })} />
            <input className="form-control" type="date" style={{ width: 150 }} value={params.to}
              onChange={(e) => setParams({ ...params, to: e.target.value, page: 1 })} />
          </div>

          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="machine_articles" label="machine articles" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No machine articles found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'machine', label: 'Machine', render: (r) => (r.machine ? <Link to={`/machines/${r.machine.id}`} style={{ fontWeight: 600 }}>{r.machine.name}</Link> : '—') },
              { key: 'article_no', label: 'Article No', render: (r) => <b>{r.article_no}</b> },
              { key: 'article_name', label: 'Article Name', render: (r) => r.article_name || '—' },
              { key: 'loaded_date', label: 'Loaded Date', render: (r) => formatDate(r.loaded_date) },
              { key: 'released_date', label: 'Released Date', render: (r) => r.released_date ? formatDate(r.released_date) : '—' },
              { key: 'days', label: 'Days', align: 'right', render: (r) => (
                <span className={r.days > 0 ? '' : 'text-muted'}>
                  <b>{r.days ?? 0}</b>
                  {r.released_date ? '' : ' days (on machine)'}
                </span>
              ) },
              { key: 'status', label: 'Status', render: (r) => r.released_date
                ? <span className="badge badge-gray">Released</span>
                : <span className="badge badge-green">On Machine</span> },
              { key: 'remarks', label: 'Remarks', render: (r) => r.remarks || '—' },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {!r.released_date && hasPermission('manage-machine-articles') && (
                    <button className="btn-icon" title="Release" onClick={() => openRelease(r)}><FiDownload /></button>
                  )}
                  {hasPermission('delete-machine-articles') && (
                    <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>

      <Modal open={loadModal} onClose={() => { setErrors({}); setLoadModal(false); }} title="Load Article on Machine"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setLoadModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleLoad} disabled={saving}>{saving ? 'Loading...' : 'Load Article'}</button>
        </>}
      >
        <div className="grid-2">
          <div className="form-group"><label className="form-label">Machine <span className="req">*</span></label>
            <SearchableSelect
              options={machines.map((m) => ({ value: m.id, label: `${m.name} (${m.head_type}h)` }))}
              value={loadForm.machine_id}
              onChange={(v) => { setLoadForm({ ...loadForm, machine_id: v }); if (errors.machine_id) setErrors((p) => ({ ...p, machine_id: '' })); }}
              isInvalid={!!errors.machine_id}
              placeholder="-- Select machine --"
            />
            {errors.machine_id && <div className="invalid-feedback">{errors.machine_id}</div>}</div>
          <div className="form-group"><label className="form-label">Article No <span className="req">*</span></label>
            <SearchableSelect
              options={articleOptions}
              value={loadForm.article_no}
              onChange={(v) => {
                setLoadForm({ ...loadForm, article_no: v });
                const opt = articleOptions.find((o) => o.value === v);
                if (opt && opt.name) setLoadForm((p) => ({ ...p, article_no: v, article_name: opt.name }));
                if (errors.article_no) setErrors((p) => ({ ...p, article_no: '' }));
              }}
              isInvalid={!!errors.article_no}
              placeholder="-- Select article from gatepass --"
            />
            {errors.article_no && <div className="invalid-feedback">{errors.article_no}</div>}</div>
          <div className="form-group"><label className="form-label">Article Name</label>
            <input className="form-control" value={loadForm.article_name}
              onChange={(e) => setLoadForm({ ...loadForm, article_name: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Loaded Date <span className="req">*</span></label>
            <input className={`form-control ${errors.loaded_date ? 'is-invalid' : ''}`} type="date" value={loadForm.loaded_date}
              onChange={(e) => { setLoadForm({ ...loadForm, loaded_date: e.target.value }); if (errors.loaded_date) setErrors((p) => ({ ...p, loaded_date: '' })); }} />
            {errors.loaded_date && <div className="invalid-feedback">{errors.loaded_date}</div>}</div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Remarks</label>
            <textarea className="form-control" rows={2} value={loadForm.remarks}
              onChange={(e) => setLoadForm({ ...loadForm, remarks: e.target.value })} /></div>
        </div>
      </Modal>

      <Modal open={releaseModal} onClose={() => { setErrors({}); setReleaseModal(false); }} title={`Release Article — ${releaseTarget?.article_no || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setReleaseModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleRelease} disabled={saving}>{saving ? 'Releasing...' : 'Release Article'}</button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Released Date <span className="req">*</span></label>
          <input className={`form-control ${errors.released_date ? 'is-invalid' : ''}`} type="date" value={releaseForm.released_date}
            onChange={(e) => { setReleaseForm({ released_date: e.target.value }); if (errors.released_date) setErrors((p) => ({ ...p, released_date: '' })); }} />
          {errors.released_date && <div className="invalid-feedback">{errors.released_date}</div>}
        </div>
        {releaseTarget && (
          <div className="alert-banner" style={{ marginTop: 10, fontSize: 13 }}>
            Machine: <b>{releaseTarget.machine?.name}</b> &nbsp;|&nbsp; Loaded: <b>{formatDate(releaseTarget.loaded_date)}</b>
          </div>
        )}
      </Modal>
    </div>
  );
}
